import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Popup from "../../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS } from "../../popup/popupLayoutSpec";
import Heading from "../Typography/Heading";
import Paragraph from "../Typography/Paragraph";
import SpecialText from "../Typography/SpecialText";
import MainButton from "../MainButton/MainButton";
import StepBackButton from "../StepBackButton/StepBackButton";
import TextArea from "../InputField/TextArea";
import FieldDropdown from "../InputField/FieldDropdown";
import DropzoneUploader from "../DropzoneUploader/DropzoneUploader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { disableScroll, enableScroll } from "../../../utils/scrollUtils";
import { runTextPipeline } from "../../../utils/contentSafety";

// Image Container Component
const ImageContainer = React.memo(({ url, name, isDragging }) => (
  <div className="relative size-full">
    <div 
      className="size-full bg-cover bg-center rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200"
      style={{ 
        backgroundImage: `url(${url})`,
        opacity: isDragging ? 0.5 : 1
      }}
    />
    <div className="absolute inset-0 bg-transparent" />
  </div>
));

// Sortable Item Component
const SortableItem = ({ id, item, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : 'transform 200ms cubic-bezier(0.2, 0, 0, 1)',
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  }), [transform, transition, isDragging]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="relative group">
        <div className="size-[60px]">
          <ImageContainer 
            url={item.url || ''} 
            name={item?.file?.name || 'Uploaded image'}
            isDragging={isDragging}
          />
          <button 
            type="button" 
            onClick={() => onDelete(index)}
            className="absolute -top-2 -right-2 size-5 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <i className="fi fi-rs-cross text-[10px] text-offblack"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

// Validation schema for the report form (context required only when issueType is "other")
const reportValidationSchema = Yup.object().shape({
  issueType: Yup.string().required('Please select an issue type'),
  context: Yup.string()
    .when('issueType', {
      is: 'other',
      then: (schema) =>
        schema
          .min(10, 'Context must be at least 10 characters')
          .required('Context is required')
          .test('contentSafety', function (value) {
            if (!value || !String(value).trim()) return true;
            const result = runTextPipeline(value, 'report');
            if (result.valid) return true;
            return this.createError({ message: result.message });
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
  screenshots: Yup.array().max(5, 'Maximum 5 screenshots allowed')
});

const PostReportForm = ({ 
  isOpen, 
  onClose, 
  postData = null,
  reportType = "post" // "post" or "profile"
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showCrossButton, setShowCrossButton] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [images, setImages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Issue types for Step 1 - different based on report type
  const getIssueTypes = () => {
    if (reportType === "profile") {
      return [
        { label: "Fake or misleading profile", value: "fake_profile" },
        { label: "Unlicensed or false credentials", value: "false_credentials" },
        { label: "Harassment or abusive contact", value: "harassment" },
        { label: "Scam or suspicious behavior", value: "scam_behavior" },
        { label: "Other", value: "other" }
      ];
    } else {
      return [
        { label: "Inappropriate or explicit images", value: "inappropriate_images" },
        { label: "Misleading or false information", value: "misleading_info" },
        { label: "Offensive or abusive content", value: "offensive_content" },
        { label: "Spam or irrelevant content", value: "spam_content" },
        { label: "Other", value: "other" }
      ];
    }
  };

  const issueTypes = getIssueTypes();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const formik = useFormik({
    initialValues: {
      issueType: "",
      context: "",
      screenshots: []
    },
    validationSchema: reportValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // TODO: Implement API call to submit report
        console.log('Submitting report:', values);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setShowConfirmation(true);
      } catch (error) {
        console.error('Error submitting report:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  // Handle image upload
  const handleUploadComplete = useCallback((uploadData) => {
    const newImages = Array.isArray(uploadData) ? uploadData : [uploadData];
    const processedImages = newImages.map((data, index) => ({
      name: data.fileName,
      url: data.url,
    }));
    
    setImages(prev => [...prev, ...processedImages]);
    
    const currentImages = formik.values.screenshots || [];
    const updatedImages = [...currentImages, ...processedImages];
    formik.setFieldValue("screenshots", updatedImages);
  }, [formik]);

  const deleteImage = useCallback((index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    formik.setFieldValue("screenshots", newImages);
  }, [images, formik]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = images.findIndex((_, index) => `item-${index}` === active.id);
      const newIndex = images.findIndex((_, index) => `item-${index}` === over.id);
      
      const newItems = arrayMove(images, oldIndex, newIndex);
      
      setImages(newItems);
      formik.setFieldValue("screenshots", newItems);
    }
    
    setIsDragging(false);
    setActiveId(null);
  }, [images, formik]);

  const items = useMemo(() => 
    images.map((_, index) => `item-${index}`),
    [images]
  );

  // "Provide more context" step only when reason is Other; max step depends on that
  const showContextStep = formik.values.issueType === "other";
  const maxStep = reportType === "profile" ? 3 : showContextStep ? 2 : 1;

  const handleNext = () => {
    if (currentStep === 1 && !showContextStep) {
      // Skip context step: post -> submit; profile -> go to step 3 (screenshots)
      if (reportType === "profile") {
        setCurrentStep(3);
      } else {
        formik.handleSubmit();
      }
      return;
    }
    if (currentStep < maxStep) {
      setCurrentStep(currentStep + 1);
    } else {
      formik.handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep === 3 && reportType === "profile" && !showContextStep) {
      setCurrentStep(1); // Skip step 2 when reason is not Other
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle dropdown changes
  const handleIssueTypeChange = (e) => {
    formik.setFieldValue('issueType', e.value);
  };

  // Get step title based on current step and report type
  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return reportType === "profile" ? "What's the issue with this profile?" : "What's the issue with this post?";
      case 2:
        return "Provide more context";
      case 3:
        return reportType === "profile" ? "Add screenshots (optional)" : "";
      default:
        return "";
    }
  };

  // Check if current step is valid
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return !!formik.values.issueType;
      case 2:
        return formik.values.context.length >= 10;
      case 3:
        return true; // Screenshots are optional
      default:
        return false;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderIssueTypeStep();
      case 2:
        return renderContextStep();
      case 3:
        return reportType === "profile" ? renderScreenshotsStep() : null;
      default:
        return null;
    }
  };

  // Step 1: Issue Type Selection
  const renderIssueTypeStep = () => (
    <div className="w-full">
      <FieldDropdown
        value={formik.values.issueType}
        onChange={handleIssueTypeChange}
        onBlur={() => formik.setFieldTouched("issueType", true)}
        options={issueTypes}
        optionLabel="label"
        optionValue="value"
        placeholder="Select an issue type"
        variant="general"
        fullWidth
        error={
          formik.touched.issueType && formik.errors.issueType
            ? formik.errors.issueType
            : null
        }
      />
    </div>
  );

  // Step 2: Context
  const renderContextStep = () => (
    <div className="w-full">
      <div className="relative">
        <TextArea
          variant="general"
          fullWidth
          label={null}
          placeholder={reportType === "profile" 
            ? "Please provide additional details about why you're reporting this profile..." 
            : "Please provide additional details about why you're reporting this post..."
          }
          name="context"
          value={formik.values.context}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className="min-h-[120px] resize-none pr-16"
          style={{ resize: 'none' }}
        />
        {/* <div className="absolute -top-3 right-1">
          <SpecialText variant="Counter" className="text-mediumgray">
            {formik.values.context.length}/500
          </SpecialText>
        </div> */}
      </div>
      {formik.touched.context && formik.errors.context && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.context}
        </SpecialText>
      )}
    </div>
  );

  // Step 3: Screenshots (Profile reports only)
  const renderScreenshotsStep = () => (
    <div className="w-full">
      {/* <div className="mb-4">
        <Paragraph variant="BodyCompact" className="text-mediumgray text-center">
          Upload screenshots or images that support your report
        </Paragraph>
      </div> */}
      
      <div className="w-full">
        {/* Uploaded Images */}
        {images.length > 0 && (
          <div className="mt-1 mb-5 w-full">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items}
                strategy={rectSortingStrategy}
              >
                <div className="flex flex-wrap gap-7">
                  {images.map((item, index) => (
                    <SortableItem
                      key={`item-${index}`}
                      id={`item-${index}`}
                      item={item}
                      index={index}
                      onDelete={deleteImage}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        <DropzoneUploader
          isImage={true}
          maxFiles={5}
          loadingType="progress"
          onUploadComplete={handleUploadComplete}
          currentFileCount={images.length}
          applyFieldStyles={false}
          fullWidth
        />
      </div>
      {formik.touched.screenshots && formik.errors.screenshots && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.screenshots}
        </SpecialText>
      )}
    </div>
  );

  // Handle popup close
  const handleClose = () => {
    onClose();
    formik.resetForm();
    setCurrentStep(1);
    setShowConfirmation(false);
    setImages([]);
  };

  // Update body overflow when popup is open
  useEffect(() => {
    if (isOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [isOpen]);

  // Cleanup effect to ensure scrolling is restored on unmount
  useEffect(() => {
    return () => {
      enableScroll();
    };
  }, []);

  if (!isOpen) return null;

  // Render confirmation step
  if (showConfirmation) {
    return (
      <Popup
        setTrigger={() => handleClose()}
        ShowCrossButton={showCrossButton}
        onClose={handleClose}
      >
        <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <div className={POPUP_FORM_CONTENT_CLASS}>
            <Heading
              variant={"h6"}
              className={
                "flex items-center justify-center text-center text-offblack"
              }
            >
              Confirmation Message
            </Heading>
            <div className="mt-10 xl:mt-8 w-full">
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  <Paragraph
                    variant={"BodyCompact"}
                    className={"text-offblack text-center"}
                  >
                    {reportType === "profile" 
                      ? "Thank you. Your report has been submitted. Our moderation team will assess this profile and take appropriate action in line with our Terms & Conditions and Community Guidelines. You will be notified once the review is complete."
                      : "Thank you. Your report has been submitted. Our moderation team will investigate the issue and take appropriate action in line with our Terms & Conditions and Community Guidelines. You will be notified once the review is complete."
                    }
                  </Paragraph>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      setTrigger={() => handleClose()}
      ShowCrossButton={showCrossButton}
      onClose={handleClose}
    >
      <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <Heading variant="h6" className="flex items-center justify-center text-center">
            {getStepTitle()}
          </Heading>
          <div className="mt-10 xl:mt-8 w-full">
            {renderStepContent()}
            <div className="flex items-center justify-between mt-10 w-full">
              <StepBackButton onClick={handleBack} />
              <MainButton
                variant="small"
                onClick={handleNext}
                disabled={!isCurrentStepValid() || isSubmitting}
              >
                {currentStep === maxStep ? (isSubmitting ? 'Submit' : 'Submit') : 'Next'}
              </MainButton>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default PostReportForm; 